import type { TimeInput } from './types';
import { toDate } from './isomorphic/internal/coerce';
import { safeFormat } from './isomorphic/internal/safe-format';

export interface FormatLogicalDateOptions {
    /** Drop the year ("Mar 15"). */
    omitYear?: boolean;
    /**
     * Two-digit year ("Mar 15, 26") for a dense column where the full year costs
     * width it cannot spare. Ignored when `omitYear` is set.
     */
    shortYear?: boolean;
    /** Include the weekday ("Sun, Mar 15, 2026"). */
    weekday?: boolean;
    /** Spell the month out ("March 15, 2026"), for prose such as a legal page's effective date. */
    longMonth?: boolean;
    /** Fallback string for invalid/empty input. Default "—". */
    placeholder?: string;
}

/**
 * Format a **logical / calendar date** — a day with no time-of-day and no zone.
 * These MUST render UTC-anchored so the literal day never shifts across the
 * viewer's zone, and they NEVER carry a zone label.
 *
 * **The discriminator is the STORED COLUMN TYPE, not the noun and not the
 * rendered shape** (BUG-1591). A value is a logical date iff it comes from a
 * `date` column. If it comes from a `timestamptz` it is an *instant*, its
 * calendar day is zone-dependent, and it must be resolved in the governing zone
 * via {@link formatEventTime} or `formatInZone` — never here.
 *
 * Genuine `DATE` columns, and therefore this function's callers:
 * `invoices.auction_date` / `.due_date` / `.pickup_deadline`,
 * `settlements.auction_date` / `.paid_date` / `.last_ucc_search_date`,
 * `settlement_credits.date`, `settlement_deductions.date`,
 * `settlement_payments.date`, `customers.tax_exempt_expires_at`,
 * `finix_settlement_batches.batch_date` / `.expected_deposit_date`.
 *
 * **Not this — "auction date" names two different things.** The `auction_date`
 * above (on `invoices` / `settlements`) is a genuine `DATE` and belongs here.
 * **`auctions.start_date` is a `timestamptz` and is NOT a logical date** —
 * formatting it here renders the UTC calendar day, which for an evening sale is
 * tomorrow. That is BUG-1507: a Denver clerk printed a Pacific sale's clerk
 * sheet dated a day late.
 *
 * Accepts a `YYYY-MM-DD` string (the common case) or any {@link TimeInput};
 * always interprets the value in UTC. Subsumes the old admin date-only branch,
 * `formatTerminalDate`, and `formatUtcDate`.
 *
 * @sideEffect none — pure.
 */
export function formatLogicalDate(
    input: TimeInput,
    opts: FormatLogicalDateOptions = {},
): string {
    const d = toDate(input);
    if (!d) return opts.placeholder ?? '—';

    const fmt: Intl.DateTimeFormatOptions = {
        month: opts.longMonth ? 'long' : 'short',
        day: 'numeric',
        timeZone: 'UTC',
    };
    if (!opts.omitYear) fmt.year = opts.shortYear ? '2-digit' : 'numeric';
    if (opts.weekday) fmt.weekday = 'short';

    return safeFormat(d, fmt);
}
